import {Injectable, ErrorHandler, Injector} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';

import {
  AppError,
  APPERROR_NO_USER_CONFIGS_DATA,
  APPERROR_NO_USER_ROLE
} from './app-error';
import {Router} from '@angular/router';
import {NotificationService} from '../../shared/services';
import {ErrorReportService} from '../services';
import {environment} from '../../../environments/environment';
import {
  V1ApiHttpErrorResponse,
  V1HttpErrorInterceptor
} from '../interceptors/v1-http-error-interceptor.service';

@Injectable()
export class AppErrorHandler extends ErrorHandler {

  private notificationsService: NotificationService;
  private router: Router;
  private errorReportService: ErrorReportService;

  constructor(private injector: Injector) {
    super();
  }

  handleError(error: Error | AppError | V1ApiHttpErrorResponse | HttpErrorResponse) {

    this.notificationsService = this.injector.get(NotificationService);
    this.router = this.injector.get(Router);
    this.errorReportService = this.injector.get(ErrorReportService);

    if (error instanceof V1HttpErrorInterceptor || error instanceof HttpErrorResponse) {
      // this.WebServiceErrorHandler(error.error);
      // gli errori http sono gestiti tramite un altro handler
      return;
    }

    // console.log('Errore', error);

    if (error instanceof AppError) {
      this.AppErrorHandler(error);
      return;
    }

    let displayMessage = 'Si è verificato un errore';
    if (!environment.production) {
      displayMessage += ' Dettaglio in console.';
      this.notificationsService.danger(displayMessage);
    }

    // Arricchisco l'errore con informazioni aggiuntive
    const enrichedError = this.enrichError(error);
    this.reportError(enrichedError);

    super.handleError(error);
  }

  /**
   * Funzione per la gestione degli errori dell APP
   */
  private AppErrorHandler(err: AppError) {
    switch (err.errorCode) {
      case APPERROR_NO_USER_CONFIGS_DATA:
        this.notificationsService.danger(err.message);
        this.reportError(err);
        break;
      case APPERROR_NO_USER_ROLE:
        this.router.navigateByUrl('/login');
        break;
      default:
        this.notificationsService.danger(err.message);
        if (!environment.production) {
          console.error(err);
        }
        // In caso di error non gestito lo notifico al server per la memorizzazione
        this.reportError(err);
        break;
    }
  }

  /**
   * Arricchisce l'errore con informazioni aggiuntive per il debug
   */
  private enrichError(error: any) {

    const fullStack = {
      // Cattura lo stack trace non minificato se disponibile
      originalStack: error.stack,
      // Informazioni sulla zone.js se disponibile
      zoneAwareStack: (error as any).zoneAwareStack,
      // Informazioni sul componente Angular se disponibile
      ngDebugContext: this.extractAngularContext(error),
      // Informazioni sull'errore RxJS se disponibile
      rxjsContext: this.extractRxjsContext(error),
      // Informazioni Ionic specifiche
      ionicContext: this.extractIonicContext(error),
      sourceMapData: {
        needsDecoding: environment.production,
        originalStack: error?.originalStack || error?.stack,
        zoneAwareStack: error?.zoneAwareStack || null
      }
    };

    const enrichedError = {
      ...error,
      userAgent: navigator.userAgent,
      stack: fullStack ?? null,
    };

    return enrichedError;
  }

  private extractIonicContext(error: any): any {
    try {
      const ionicInfo = {
        currentRoute: this.router?.url,
        routerState: this.router?.routerState?.snapshot?.url,

        // Informazioni su modal e overlay attivi
        overlayContext: this.getActiveOverlays(),

        // Informazioni sulla memoria se disponibili
        memoryInfo: (performance as any).memory ? {
          usedJSHeapSize: (performance as any).memory?.usedJSHeapSize,
          totalJSHeapSize: (performance as any).memory?.totalJSHeapSize,
          jsHeapSizeLimit: (performance as any).memory?.jsHeapSizeLimit
        } : null
      };

      return ionicInfo;
    } catch (e) {
      return null;
    }
  }


  /**
   * Estrae il contesto Angular dall'errore se disponibile
   */
  private extractAngularContext(error: any): any {
    try {
      if (error.ngDebugContext) {
        return {
          component: error.ngDebugContext.component?.constructor?.name,
          template: error.ngDebugContext.view?.def?.template,
          element: error.ngDebugContext.elDef?.element?.name
        };
      }
    } catch (e) {
      // Ignora errori nell'estrazione del contesto
    }
    return null;
  }

  /**
   * Estrae informazioni RxJS dall'errore se disponibile
   */
  private extractRxjsContext(error: any): any {
    try {
      if (error.source || error.operator) {
        return {
          operator: error.operator,
          source: error.source?.constructor?.name
        };
      }
    } catch (e) {
      // Ignora errori nell'estrazione del contesto RxJS
    }
    return null;
  }

  /**
   * Ottiene informazioni sui modal/overlay attivi
   */
  private getActiveOverlays(): any {
    try {
      const overlays = document.querySelectorAll('ion-modal, ion-popover, ion-action-sheet, ion-alert, ion-loading, ion-toast');
      return Array.from(overlays).map(overlay => ({
        tagName: overlay.tagName.toLowerCase(),
        isPresented: (overlay as any).isOpen || false,
        id: overlay.id || null
      }));
    } catch (e) {
      return [];
    }
  }

  private reportError(error) {
    // this.errorReportService.sendErrorToApi$(error).toPromise();
  }

}
